import { FC, useCallback, useContext } from "react";
import { Route } from "nextjs-routes";
import {
  ModuleBuilderContext,
  ModuleBuilderContextState,
} from "@src/features/libraryItem/module/contexts/ModuleBuilderContext";
import { getPromptScreenId } from "@src/features/libraryItem/course/hooks/useCourseScreensAtom";
import { ActiveClientFragment } from "@src/lib/ui/ActiveClientsAvatarStack.generated";
import { useAtomCallback } from "jotai/utils";
import { moduleVersionAtom } from "@src/features/libraryItem/module/atoms";
import { courseVersionAtom } from "@src/features/libraryItem/course/atoms";

import ActiveClientsAvatarStack from "@src/lib/ui/ActiveClientsAvatarStack";

type Props = {
  activeClients: ActiveClientFragment[];
  size: number;
};

const LibraryItemActiveClientsAvatarStack: FC<Props> = ({
  activeClients,
  size,
}) => {
  const moduleBuilderContext = useContext(ModuleBuilderContext);
  const getRouteFromActiveClients = useAtomCallback(
    useCallback(
      (
        get,
        _set,
        moduleBuilderContext: ModuleBuilderContextState | undefined,
        activeClient: ActiveClientFragment,
        libraryItemDetailPageEnabled?: boolean,
      ): Route | undefined => {
        const moduleVersion = get(moduleVersionAtom);
        const courseVersion = get(courseVersionAtom);
        if (moduleBuilderContext) {
          if (
            activeClient.moduleVersionId &&
            activeClient.moduleContentMembershipId
          ) {
            if (
              libraryItemDetailPageEnabled &&
              moduleVersion?.module.libraryItem
            ) {
              return {
                pathname: "/library/library-item/[id]",
                query: {
                  id: moduleVersion?.module.libraryItem.id,
                  m: activeClient.moduleContentMembershipId,
                  screenId: activeClient.promptUuid
                    ? getPromptScreenId(activeClient.promptUuid)
                    : undefined,
                },
              };
            } else {
              return {
                pathname: "/builder/module-version/[id]",
                query: {
                  id: activeClient.moduleVersionId,
                  m: activeClient.moduleContentMembershipId,
                  screenId: activeClient.promptUuid
                    ? getPromptScreenId(activeClient.promptUuid)
                    : undefined,
                },
              };
            }
          }
        } else if (courseVersion) {
          if (activeClient.courseVersionId) {
            if (
              libraryItemDetailPageEnabled &&
              courseVersion?.course.libraryItem
            ) {
              return {
                pathname: "/library/library-item/[id]",
                query: {
                  id: courseVersion?.course.libraryItem.id,
                  screenId: activeClient.promptUuid
                    ? getPromptScreenId(activeClient.promptUuid)
                    : undefined,
                },
              };
            } else {
              return {
                pathname: "/builder/course-version/[id]",
                query: {
                  id: activeClient.courseVersionId.toString(),
                  screenId: activeClient.promptUuid
                    ? getPromptScreenId(activeClient.promptUuid)
                    : undefined,
                },
              };
            }
          }
        }
      },
      [],
    ),
  );
  return (
    <ActiveClientsAvatarStack
      clients={activeClients}
      size={size}
      getRoute={(client) =>
        getRouteFromActiveClients(moduleBuilderContext, client)
      }
    />
  );
};

export default LibraryItemActiveClientsAvatarStack;
